import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Typography,
  message,
  Space,
  Tag,
  Alert,
  Popconfirm
} from 'antd';
import { ReloadOutlined, LinkOutlined } from '@ant-design/icons';
import devToolsService, { ProductCustomerPair } from '../services/devToolsService.ts';

const { Title, Text } = Typography;

interface MissingItem {
  product_id: number;
  customer_id: number;
  internal_product_code: string | null;
  client_product_code: string | null;
  product_description: string | null;
  customer_company: string | null;
  stock_rows: number;
  total_quantity: number;
}

const rowKeyOf = (r: MissingItem) => `${r.product_id}-${r.customer_id}`;

const MissingCustomerAssociations: React.FC = () => {
  const [items, setItems] = useState<MissingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await devToolsService.getMissingCustomerAssociations();
      const list: MissingItem[] = data?.items || [];
      setItems(list);
      setSelectedKeys(list.map(rowKeyOf)); // default: tutte selezionate
    } catch (error) {
      console.error('Errore caricamento associazioni mancanti:', error);
      message.error('Errore nel caricamento delle associazioni mancanti');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    const pairs: ProductCustomerPair[] = items
      .filter((r) => selectedKeys.includes(rowKeyOf(r)))
      .map((r) => ({ product_id: r.product_id, customer_id: r.customer_id }));

    if (pairs.length === 0) {
      message.warning('Seleziona almeno una riga');
      return;
    }

    setSaving(true);
    try {
      const res = await devToolsService.createMissingCustomerAssociations(pairs);
      message.success(`Associazioni create: ${res.created}, già esistenti: ${res.skipped}`);
      if (res.errors && res.errors.length > 0) {
        message.warning(`${res.errors.length} righe non elaborate (prodotto/cliente non valido)`);
      }
      await loadData();
    } catch (error: any) {
      console.error('Errore creazione associazioni:', error);
      message.error(error.response?.data?.detail || 'Errore nella creazione delle associazioni');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Codice Interno', dataIndex: 'internal_product_code', key: 'internal_product_code',
      render: (v: string) => <Text code>{v || '-'}</Text> },
    { title: 'Codice Cliente', dataIndex: 'client_product_code', key: 'client_product_code',
      render: (v: string) => v || '-' },
    { title: 'Descrizione', dataIndex: 'product_description', key: 'product_description',
      render: (v: string) => v || '-' },
    { title: 'Cliente da associare', dataIndex: 'customer_company', key: 'customer_company',
      render: (v: string) => <Tag color="blue">{v || '-'}</Tag> },
    { title: 'Righe Stock', dataIndex: 'stock_rows', key: 'stock_rows', align: 'right' as const, width: 110 },
    { title: 'Q.tà Totale', dataIndex: 'total_quantity', key: 'total_quantity', align: 'right' as const, width: 120 }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2}><LinkOutlined /> Associazioni Cliente Mancanti</Title>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Prodotti che nello stock risultano intestati a un cliente (product_stocks.customer_id) ma che non hanno la relativa associazione in anagrafica (product_customers). Seleziona le righe per cui creare l'associazione mancante."
        />

        <Space style={{ marginBottom: 16 }}>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            Aggiorna
          </Button>
          <Popconfirm
            title={`Creare ${selectedKeys.length} associazioni prodotto-cliente?`}
            onConfirm={handleCreate}
            okText="Crea"
            cancelText="Annulla"
            disabled={selectedKeys.length === 0}
          >
            <Button type="primary" icon={<LinkOutlined />} loading={saving} disabled={selectedKeys.length === 0}>
              Crea associazioni selezionate ({selectedKeys.length})
            </Button>
          </Popconfirm>
          <Text type="secondary">{items.length} associazioni mancanti trovate</Text>
        </Space>

        <Table
          rowKey={rowKeyOf}
          columns={columns}
          dataSource={items}
          loading={loading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true }}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys) => setSelectedKeys(keys)
          }}
          locale={{ emptyText: 'Nessuna associazione mancante: i dati sono coerenti' }}
        />
      </Card>
    </div>
  );
};

export default MissingCustomerAssociations;
